import { useId, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import BackpackOutlinedIcon from '@mui/icons-material/BackpackOutlined';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import FlightClassOutlinedIcon from '@mui/icons-material/FlightClassOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import WeekendOutlinedIcon from '@mui/icons-material/WeekendOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  AirPenalties,
  AirSearchResponse,
  BaggageAllowance,
  CancellationPenalty,
  ChangePenalty,
  FlightSegment,
  OriginDestinationOption,
  PenaltyAmount,
  SelectableFare,
  TimeToDeparture,
} from '../../../api/types/air';
import { searchAirCombinable } from '../../../api/endpoints/air';
import { getOfferDocument } from '../../../api/endpoints/documents';
import { openHtmlDocument } from '../../../components/reservation/documentHtml';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { AirlineLogo, airlineCodeOf, airlineNameOf } from '../../../components/AirlineLogo';
import { OutOfBudgetChip, OutOfBudgetText } from '../../../components/booking/OutOfBudgetChip';

/** `null` when the allowance carries no usable data — callers skip the line
 *  entirely rather than showing an empty "Bagaj:" label. Prefers the provider's own
 *  `kilosString` display text when present (see api/types/air.ts's `BaggageAllowance`
 *  doc comment), otherwise composes "N parça, M kg" from the numeric fields. */
function formatBaggage(allowance?: BaggageAllowance): string | null {
  if (!allowance) return null;
  if (allowance.kilosString) return allowance.kilosString;
  const parts: string[] = [];
  if (allowance.pieces) parts.push(`${allowance.pieces} parça`);
  if (allowance.kilos) parts.push(`${allowance.kilos} kg`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** Tutarı tr-TR biçiminde ("1.936,74 TL") verir; `TRY` → "TL" kısaltması, diğer
 *  para birimleri kodu ile. */
function formatMoney(amount?: number | null, currency?: string | null): string {
  if (amount == null) return '';
  const n = amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cur = currency === 'TRY' ? 'TL' : currency;
  return cur ? `${n} ${cur}` : n;
}

/** Fare kartında gösterilecek fiyat: `currency` TRY değilse ve havayolundan gerçek TRY
 *  tutarı (`totalFareTRY`) geldiyse onu ana fiyat, yabancı para tutarını ikincil satır
 *  yapar; aksi hâlde `totalFare`'ı kendi para biriminde gösterir. */
function fareDisplayPrice(fare: SelectableFare): { primary: string; secondary?: string } {
  const cur = fare.currency ?? 'TRY';
  if (cur !== 'TRY' && fare.totalFareTRY) {
    return {
      primary: formatMoney(fare.totalFareTRY, 'TRY'),
      secondary: fare.totalFare != null ? formatMoney(fare.totalFare, cur) : undefined,
    };
  }
  return { primary: fare.totalFare != null ? formatMoney(fare.totalFare, cur) : 'Fiyat yok' };
}

/** Filtre/sıralama için option'ın en düşük TRY fiyatı — yabancı para fare'lerde
 *  `totalFareTRY` tercih edilir ki fiyat slider'ı ve sıralama TL üzerinden tutarlı olsun. */
function farePriceTRY(fare: SelectableFare): number | null {
  if (fare.currency && fare.currency !== 'TRY' && fare.totalFareTRY) return fare.totalFareTRY;
  return fare.totalFare ?? null;
}

/** Kartın "…'den başlayan" özet fiyatı için: en düşük TRY tutarlı fare. Hepsi fiyatsızsa
 *  ilk fare'e düşer; boş dizide `undefined`. */
function cheapestFare(fares: SelectableFare[]): SelectableFare | undefined {
  if (fares.length === 0) return undefined;
  return fares.reduce((best, f) => {
    const p = farePriceTRY(f);
    const bp = farePriceTRY(best);
    if (p == null) return best;
    if (bp == null) return f;
    return p < bp ? f : best;
  });
}

/**
 * GDS itinerary'si markalı `selectableFares` döndürmediğinde (yalnız `pricingInfo`) —
 * legacy `International.cshtml`'in `IsSelectableFare == false` dalı gibi — itinerary'nin
 * `pricingInfo`/`totalFare`'ından tek bir seçilebilir "ücret" türetir. `fareCode` yok:
 * allocate `productId` ile yapılır (bkz. api/types/air.ts `AllocateItem`). Fiyat bilgisi
 * hiç yoksa `null` döner (çağıran "seçilebilir ücret yok" metnine düşer).
 */
function synthesizeFareFromPricing(option: OriginDestinationOption): SelectableFare | null {
  const pricing = option.pricingInfo;
  const totalFare = pricing?.totalFare ?? option.totalFare ?? null;
  if (totalFare == null) return null;
  return {
    fareCode: option.fareCode,
    fareName: option.fareName ?? option.fareInfoType ?? 'Standart ücret',
    fareInfoType: option.fareInfoType,
    totalFare,
    totalFareTRY: pricing?.totalFareTRY,
    currency: pricing?.currency ?? undefined,
    cabin: option.flightSegments?.[0]?.cabinType,
    bookingClass: option.flightSegments?.[0]?.bookingClass,
    isOutBudget: option.isOutBudget,
    maxBudget: option.maxBudget,
  };
}

/** Kurumsal politika bütçe aşımı — ücret ya da itinerary düzeyinde `isOutBudget: true`. */
function isFareOutOfBudget(fare: SelectableFare, option?: OriginDestinationOption): boolean {
  return fare.isOutBudget === true || option?.isOutBudget === true;
}

/** `timeUnit`/`timePeriodCondition` sağlayıcı-üretimi serbest metin kodlar, belgelenmiş
 *  bir enum değil (bkz. api/types/air.ts). Gözlenen değerler dar bir küme (`HOUR`/`DAY` +
 *  `MORE`/`LESS`); bunları Türkçeye çeviriyoruz, tanımadığımız kod olduğu gibi geçer.
 *  `MORE` = kalkışa o süreden fazla var (erken), `LESS` = o süreden az kaldı (kalkışa yakın). */
const TIME_UNIT_TR: Record<string, string> = {
  HOUR: 'saat',
  HOURS: 'saat',
  DAY: 'gün',
  DAYS: 'gün',
  MINUTE: 'dakika',
  MINUTES: 'dakika',
};

function formatTimeToDeparture(t?: TimeToDeparture): string {
  if (t?.timeAmount == null) return '';
  const unit = t.timeUnit ? TIME_UNIT_TR[t.timeUnit.toUpperCase()] ?? t.timeUnit : '';
  const span = [t.timeAmount, unit].filter(Boolean).join(' ');
  switch (t.timePeriodCondition?.toUpperCase()) {
    case 'MORE':
      return `kalkışa ${span}+ kala`;
    case 'LESS':
      return `kalkışa <${span} kala`;
    default:
      return [span, t.timePeriodCondition].filter(Boolean).join(' ');
  }
}

/** `0` (ör. `FixedAmount.Amount: 0, CurrencyCode: ""`) gerçek bir ücret değil —
 *  `null` dönerek çağıranın "ücretsiz" metnine düşmesini sağlıyoruz. */
function formatPenaltyAmount(amount?: PenaltyAmount): string | null {
  if (amount?.percentage?.amount) return `%${amount.percentage.amount}`;
  const fixed = amount?.fixedAmount?.amount;
  if (fixed != null && fixed > 0) {
    return `${fixed}${amount?.fixedAmount?.currencyCode ? ` ${amount.fixedAmount.currencyCode}` : ''}`;
  }
  return null;
}

function formatChangePenalty(c: ChangePenalty): string {
  if (c.penalty) return c.penalty;
  if (c.isChangeable === false) return 'Değişikliğe kapalı';
  const amount = formatPenaltyAmount(c.percentageOrFixedAmount);
  const window = formatTimeToDeparture(c.timeToDeparture);
  const base = amount ? `Değişiklik ücreti ${amount}` : 'Değişiklik ücretsiz';
  return window ? `${base} (${window})` : base;
}

function formatCancellationPenalty(c: CancellationPenalty): string {
  if (c.penalty) return c.penalty;
  if (c.isRefundable === false) return 'İade edilemez';
  const amount = formatPenaltyAmount(c.percentageOrFixedAmount);
  const window = formatTimeToDeparture(c.timeToDeparture);
  const base = amount ? `İptal kesintisi ${amount}` : 'İade edilebilir';
  return window ? `${base} (${window})` : base;
}

/** `change` = uçuş değişikliği kuralı, `cancel` = iptal/iade kuralı — `FareCard` her
 *  satırın yanına konuya uygun ikon koymak için ayrımı taşır. */
type PenaltyLine = { kind: 'change' | 'cancel'; text: string };

/** Both arrays can (per swagger) hold multiple tiers — e.g. a cheaper change fee far
 *  from departure, a steeper one close to it — so every tier gets its own line
 *  rather than only showing the first. Aynı metne inen tier'lar (ör. her biri
 *  "Değişikliğe kapalı" olan 3 tier) tek satıra indirilir. */
function formatPenalties(penalties?: AirPenalties): PenaltyLine[] {
  if (!penalties) return [];
  const lines: PenaltyLine[] = [
    ...(penalties.changePenalties ?? []).map((c) => ({ kind: 'change' as const, text: formatChangePenalty(c) })),
    ...(penalties.cancellationPenalties ?? []).map((c) => ({
      kind: 'cancel' as const,
      text: formatCancellationPenalty(c),
    })),
  ];
  const seen = new Set<string>();
  const out: PenaltyLine[] = [];
  for (const line of lines) {
    const key = `${line.kind}|${line.text}`;
    if (line.text && !seen.has(key)) {
      seen.add(key);
      out.push(line);
    }
  }
  return out;
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** İki ISO zaman damgası arası dakika farkı — aktarma bekleme süresi ve toplam yolculuk
 *  süresi için. Geçersiz/eksik değerde `null`. */
function minutesBetween(fromIso?: string, toIso?: string): number | null {
  if (!fromIso || !toIso) return null;
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 60000);
}

/** Dakikayı "2s 25dk" biçimine çevirir (legacy "X saat Y dk" karşılığı, kısa form). */
function formatHm(totalMinutes: number | null): string {
  if (totalMinutes == null || totalMinutes <= 0) return '';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return [h ? `${h}s` : '', m ? `${m}dk` : ''].filter(Boolean).join(' ') || '0dk';
}

function SegmentRow({ segment }: { segment: FlightSegment }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <AirlineLogo size={52} code={airlineCodeOf(segment)} name={airlineNameOf(segment)} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {segment.markettingAirline ?? segment.operatingAirline ?? '—'} {segment.flightNumber ?? ''}
        </Typography>
      </Stack>
      <Typography variant="body2">
        {segment.departureAirportCode ?? '?'} ({formatDateTime(segment.departureDateTime)}) →{' '}
        {segment.arrivalAirportCode ?? '?'} ({formatDateTime(segment.arrivalDateTime)})
      </Typography>
      {segment.cabinType && <Chip size="small" label={segment.cabinType} />}
      {segment.bookingClass && <Chip size="small" label={`Sınıf: ${segment.bookingClass}`} />}
    </Stack>
  );
}

/** Bir itinerary'nin segmentlerini, aralarına aktarma (bekleme süresi + havalimanı)
 *  satırları serpiştirerek gösterir; üstte "Direkt" / "N Aktarma" rozeti ve varsa
 *  ilk kalkış → son varış toplam süresi. */
function SegmentList({ segments }: { segments: FlightSegment[] }) {
  const stops = Math.max(0, segments.length - 1);
  // `journeyDuration(InMinute)` sağlayıcı tarafında itinerary geneli için verilir (bir
  // aktarmalı option'ın her iki segmentinde de aynı değer) — varsa onu kullan, yoksa
  // ilk kalkış → son varış farkından hesapla.
  const providerMinutes = segments.find((s) => (s.journeyDurationInMinute ?? 0) > 0)?.journeyDurationInMinute;
  const totalLabel =
    formatHm(providerMinutes ?? null) ||
    segments.find((s) => s.journeyDuration)?.journeyDuration ||
    formatHm(minutesBetween(segments[0]?.departureDateTime, segments[segments.length - 1]?.arrivalDateTime));

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          size="small"
          color={stops === 0 ? 'success' : 'default'}
          label={stops === 0 ? 'Direkt' : `${stops} Aktarma`}
        />
        {totalLabel && (
          <Typography variant="caption" color="text.secondary">
            Toplam süre: {totalLabel}
          </Typography>
        )}
      </Stack>
      {segments.map((segment, idx) => {
        const next = segments[idx + 1];
        const layover = next ? formatHm(minutesBetween(segment.arrivalDateTime, next.departureDateTime)) : '';
        return (
          <Box key={segment.productId ?? idx}>
            <SegmentRow segment={segment} />
            {next && (
              <Typography variant="caption" color="warning.main" sx={{ pl: 1 }}>
                {segment.arrivalAirportCode ?? '?'} aktarma{layover ? ` · ${layover} bekleme` : ''}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

/** İkon + tek satır kural/bilgi metni — `FareCard` içinde kabin, bagaj ve ceza
 *  satırları için ortak biçim. İkon `text.disabled` tonunda ve caption'la aynı boyda. */
function InfoLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box sx={{ display: 'flex', color: 'text.disabled', flexShrink: 0, '& svg': { fontSize: 16 } }}>
        {icon}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  );
}

/** A single priced fare within an itinerary — the actual thing that gets allocated
 *  (`AllocateItem.fareCode` comes from here, not from the segment's display fare code).
 *  Modal içinde paketler yan yana dizildiği için dikey kart düzeni: üstte ad/kabin +
 *  bagaj/ceza/olanak (her biri konuya uygun ikonla), altta (mt:auto ile hizalı) fiyat
 *  ve tam-genişlik seçim butonu. */
function FareCard({
  fare,
  disabled,
  selected,
  onSelect,
  allowWithoutFareCode,
  outOfBudget = false,
}: {
  fare: SelectableFare;
  disabled: boolean;
  selected?: boolean;
  onSelect: () => void;
  /** GDS itinerary'si (yalnız `PricingInfo`, `fareCode` yok) — legacy gibi boş
   *  `fareCode` ile allocate edilebilir, "Seç" butonu kilitlenmez. */
  allowWithoutFareCode?: boolean;
  /** Kurumsal bütçe aşımı — "Seç" yerine "Onaya Gönder" + "Kural Dışı Fiyat". */
  outOfBudget?: boolean;
}) {
  const carryOn = formatBaggage(fare.carryOnBaggageAllowance);
  const checked = formatBaggage(fare.checkedBaggageAllowance);
  const penaltyLines = formatPenalties(fare.penalties);
  const price = fareDisplayPrice(fare);

  // Sağlayıcıya göre çoğu null/false gelen fare imkânları — yalnızca dolu olanlar rozetlenir,
  // her biri konusuna uygun ikonla.
  const perks: { label: string; icon: ReactElement }[] = [];
  if (fare.seatSelection) perks.push({ label: 'Koltuk seçimi', icon: <EventSeatOutlinedIcon /> });
  if (fare.businessLounge) perks.push({ label: `Lounge: ${fare.businessLounge}`, icon: <WeekendOutlinedIcon /> });
  if (fare.mealName) perks.push({ label: `İkram: ${fare.mealName}`, icon: <RestaurantOutlinedIcon /> });
  if (fare.bonusMile) {
    perks.push({
      label: fare.bonusMileAmount ? `${fare.bonusMileAmount} mil` : 'Mil kazanımı',
      icon: <LoyaltyOutlinedIcon />,
    });
  }
  (fare.amenities ?? []).forEach((a) => {
    if (a.description && a.isChargeable === false) {
      perks.push({ label: a.description, icon: <CheckCircleOutlinedIcon /> });
    }
  });

  return (
    <Stack
      spacing={0.5}
      sx={{
        flex: '1 1 220px',
        minWidth: 0,
        p: 1.5,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {fare.fareName ?? fare.fareCode ?? 'Ücret'}
      </Typography>
      {fare.cabin && (
        <InfoLine icon={<FlightClassOutlinedIcon />}>
          {fare.cabin} {fare.bookingClass ? `· ${fare.bookingClass}` : ''}
        </InfoLine>
      )}
      {(carryOn || checked) && (
        <>
          <InfoLine icon={<BackpackOutlinedIcon />}>
            {carryOn ? `El bagajı: ${carryOn}` : 'El bagajı bilgisi yok'}
          </InfoLine>
          <InfoLine icon={<LuggageOutlinedIcon />}>
            {checked ? `Bagaj: ${checked}` : 'Bagaj bilgisi yok'}
          </InfoLine>
        </>
      )}
      {penaltyLines.map((line, i) => (
        <InfoLine
          key={i}
          icon={line.kind === 'change' ? <EditCalendarOutlinedIcon /> : <EventBusyOutlinedIcon />}
        >
          {line.text}
        </InfoLine>
      ))}
      {perks.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5, gap: 0.5 }}>
          {perks.map((perk, i) => (
            <Chip key={i} size="small" variant="outlined" icon={perk.icon} label={perk.label} />
          ))}
        </Stack>
      )}
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <Typography variant="subtitle1">{price.primary}</Typography>
        {price.secondary && (
          <Typography variant="caption" color="text.secondary" component="div">
            {price.secondary}
          </Typography>
        )}
        {outOfBudget && <OutOfBudgetText maxBudget={fare.maxBudget} />}
        <Button
          fullWidth
          size="small"
          sx={{ mt: 0.5 }}
          variant={selected ? 'contained' : 'outlined'}
          color={selected ? 'success' : outOfBudget ? 'warning' : 'primary'}
          disabled={disabled || (!fare.fareCode && !allowWithoutFareCode)}
          onClick={onSelect}
        >
          {selected ? 'Seçildi' : outOfBudget ? 'Onaya Gönder' : 'Seç'}
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * "Paket Seç" modalı — bir itinerary'nin `selectableFares`'ını (ya da GDS itinerary'sinde
 * `pricingInfo`'dan türetilen tek ücreti) seçilebilir satırlar olarak listeler. Ücret satırı
 * ve bütçe-aşımı ("Onaya Gönder" / "Kural Dışı Fiyat") davranışı `FareCard`'dan aynen gelir —
 * kartlar modal içinde yan yana (flex-wrap) dizilir. Seçim `onSelectFare`'e gider;
 * modalı `OptionCard` kapatır.
 */
function FareSelectionDialog({
  open,
  onClose,
  option,
  fares,
  allowWithoutFareCode,
  disabled,
  selectedFare,
  onSelectFare,
}: {
  open: boolean;
  onClose: () => void;
  option: OriginDestinationOption;
  fares: SelectableFare[];
  /** GDS itinerary'si (yalnız `pricingInfo`, `fareCode` yok) — "Seç" butonu kilitlenmez. */
  allowWithoutFareCode?: boolean;
  disabled: boolean;
  selectedFare?: SelectableFare;
  onSelectFare: (fare: SelectableFare) => void;
}) {
  const titleId = useId();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>Paket seçin</DialogTitle>
      <DialogContent dividers>
        {option.flightSegments?.length ? (
          <SegmentList segments={option.flightSegments} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Uçuş segment bilgisi yok.
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        {allowWithoutFareCode && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Bu ücretle devam edildiğinde kapsamlı paketler (upsell) sonraki adımda sunulur.
          </Typography>
        )}
        {fares.length === 0 ? (
          <Alert severity="info">Bu uçuş için seçilebilir ücret bulunamadı.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
            {fares.map((fare, idx) => (
              <FareCard
                key={`${fare.fareCode ?? fare.fareKey ?? 'fare'}-${idx}`}
                fare={fare}
                disabled={disabled}
                allowWithoutFareCode={allowWithoutFareCode}
                selected={
                  !!selectedFare &&
                  (fare.fareCode ? fare.fareCode === selectedFare.fareCode : fare === selectedFare)
                }
                outOfBudget={isFareOutOfBudget(fare, option)}
                onSelect={() => onSelectFare(fare)}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OptionCard({
  option,
  onSelectFare,
  disabled,
  selectedFare,
}: {
  option: OriginDestinationOption;
  onSelectFare: (fare: SelectableFare) => void;
  disabled: boolean;
  /** Split (iç hat gidiş-dönüş) / çok noktalı görünümde bu karttan seçilmiş ücret —
   *  dolu ise kart bu bacağın güncel seçimi, footer "Seçildi" çipini gösterir. Tek yön /
   *  uluslararası gidiş-dönüş yolunda seçim akışı ilerlettiği için kart unmount olur, prop
   *  geçilmez. */
  selectedFare?: SelectableFare;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const selectableFares = option.selectableFares ?? [];
  // `selectableFares` boşsa (GDS itinerary'si) `pricingInfo`'dan tek bir ücret türet —
  // legacy `International.cshtml`'in `IsSelectableFare == false` dalı.
  const pricingFare = useMemo(
    () => (selectableFares.length === 0 ? synthesizeFareFromPricing(option) : null),
    [option, selectableFares.length]
  );
  const dialogFares = selectableFares.length > 0 ? selectableFares : pricingFare ? [pricingFare] : [];
  const allowWithoutFareCode = selectableFares.length === 0 && !!pricingFare;

  const cheapest = cheapestFare(dialogFares);
  const showFrom = selectableFares.length > 1;
  const anyOutOfBudget = dialogFares.some((f) => isFareOutOfBudget(f, option));
  const selectedOutOfBudget = !!selectedFare && isFareOutOfBudget(selectedFare, option);

  const handlePick = (fare: SelectableFare) => {
    onSelectFare(fare);
    setDialogOpen(false);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        {option.flightSegments?.length ? (
          <SegmentList segments={option.flightSegments} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Uçuş segment bilgisi yok.
          </Typography>
        )}
      </CardContent>
      <Divider />
      <CardActions
        sx={{
          px: 2,
          py: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box role={selectedFare ? 'status' : undefined}>
          {selectedFare ? (
            <Chip
              size="small"
              color={selectedOutOfBudget ? 'warning' : 'success'}
              label={`${selectedOutOfBudget ? 'Seçildi (kural dışı): ' : 'Seçildi: '}${
                selectedFare.fareName ?? selectedFare.fareCode ?? 'Ücret'
              } — ${fareDisplayPrice(selectedFare).primary}`}
            />
          ) : dialogFares.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Seçilebilir ücret bulunamadı.
            </Typography>
          ) : (
            <Stack spacing={0.25}>
              <Typography variant="subtitle1" component="span">
                {cheapest ? fareDisplayPrice(cheapest).primary : 'Fiyat yok'}
                {showFrom && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {"'"}den başlayan
                  </Typography>
                )}
              </Typography>
              {cheapest && fareDisplayPrice(cheapest).secondary && (
                <Typography variant="caption" color="text.secondary">
                  {fareDisplayPrice(cheapest).secondary}
                </Typography>
              )}
              {anyOutOfBudget && (
                <OutOfBudgetChip />
              )}
            </Stack>
          )}
        </Box>

        {selectedFare ? (
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            aria-label="Seçili paketi değiştir"
            onClick={() => setDialogOpen(true)}
          >
            Değiştir
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            disabled={disabled || dialogFares.length === 0}
            onClick={() => setDialogOpen(true)}
          >
            Paket Seç
          </Button>
        )}
      </CardActions>

      <FareSelectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        option={option}
        fares={dialogFares}
        allowWithoutFareCode={allowWithoutFareCode}
        disabled={disabled}
        selectedFare={selectedFare}
        onSelectFare={handlePick}
      />
    </Card>
  );
}

/* ---------------------------------------------------------------------------
 * Filtre paneli — legacy arama-sonucu filtre panelinin (Direkt/Aktarmalı, fiyat
 * aralığı, kalkış/varış saat aralığı, havayolu, sıralama) karşılığı. "Fiyat tipi"
 * (Esnek/Promosyon) ve "Kurala Uyanlar" (kurumsal politika uyumu) filtreleri, ve
 * "Teklif Oluştur" e-posta akışı legacy'de görülüyor ama `SelectableFare`/
 * `OriginDestinationOption` tiplerinde bunlara karşılık gelen doğrulanmış bir alan
 * olmadığından (fare-tipi/politika bilgisi API'de modellenmemiş) buraya eklenmedi.
 * ------------------------------------------------------------------------- */

type SortOption =
  | 'asc-price'
  | 'desc-price'
  | 'dep-early-to-late'
  | 'dep-late-to-early'
  | 'arr-early-to-late'
  | 'arr-late-to-early';

const SORT_LABELS: Record<SortOption, string> = {
  'asc-price': 'Fiyata göre artan',
  'desc-price': 'Fiyata göre azalan',
  'dep-early-to-late': 'Kalkış erkenden geçe',
  'dep-late-to-early': 'Kalkış geçten erkene',
  'arr-early-to-late': 'Varış erkenden geçe',
  'arr-late-to-early': 'Varış geçten erkene',
};

/**
 * Domestic round-trip searches (`isInternational: false`) present outbound and return
 * as SEPARATE one-way options in the same flat `originDestinationOptions` list — each
 * option's segments carry `directionInd` (0=gidiş, 1=dönüş, confirmed via swagger/the
 * legacy reservation-detail view's `DirectionInd` split — see api/types/air.ts). An
 * option's own direction is its first segment's `directionInd` (a domestic one-way
 * option's segments all share one direction). International round-trip and one-way
 * searches don't set this at all, so `optionDirection` returns `undefined` for them and
 * the existing single-list combined-itinerary flow is unaffected.
 */
function optionDirection(option: OriginDestinationOption): number | undefined {
  return option.flightSegments?.[0]?.directionInd;
}

function optionPrice(option: OriginDestinationOption): number | null {
  const prices = (option.selectableFares ?? [])
    .map(farePriceTRY)
    .filter((p): p is number => p != null);
  if (prices.length) return Math.min(...prices);
  return option.pricingInfo?.totalFare ?? option.totalFare ?? null;
}

function optionDepartureDateTime(option: OriginDestinationOption): string | undefined {
  return option.flightSegments?.[0]?.departureDateTime;
}

function optionArrivalDateTime(option: OriginDestinationOption): string | undefined {
  const segments = option.flightSegments ?? [];
  return segments[segments.length - 1]?.arrivalDateTime;
}

function hourOf(value?: string): number | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() + d.getMinutes() / 60;
}

function timeOf(value?: string): number {
  if (!value) return 0;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function optionAirlines(option: OriginDestinationOption): { code: string; name: string }[] {
  const map = new Map<string, string>();
  (option.flightSegments ?? []).forEach((s) => {
    const code = s.markettingAirline ?? s.operatingAirline;
    if (!code) return;
    map.set(code, s.markettingAirlineName ?? s.operatingAirlineName ?? code);
  });
  return Array.from(map, ([code, name]) => ({ code, name }));
}

function AirlineFilterPanel({
  airlines,
  selected,
  onChange,
}: {
  airlines: { code: string; name: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const allSelected = airlines.length > 0 && airlines.every((a) => selected.has(a.code));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Havayolları
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && selected.size > 0}
            onChange={(e) => onChange(e.target.checked ? new Set(airlines.map((a) => a.code)) : new Set())}
          />
        }
        label="Tümünü seç"
      />
      {airlines.map((a) => (
        <Stack key={a.code} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.has(a.code)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(a.code);
                  else next.delete(a.code);
                  onChange(next);
                }}
              />
            }
            label={
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                <AirlineLogo size={30} code={a.code} name={a.name} showFallback={false} />
                <span>{a.name}</span>
              </Stack>
            }
          />
          <Link component="button" type="button" variant="caption" onClick={() => onChange(new Set([a.code]))}>
            sadece
          </Link>
        </Stack>
      ))}
    </Paper>
  );
}

interface FilterState {
  sortBy: SortOption;
  showDirect: boolean;
  showConnected: boolean;
  maxPrice: number;
  depHourRange: [number, number];
  arrHourRange: [number, number];
  selectedAirlines: Set<string>;
}

function optionMatchesFilters(option: OriginDestinationOption, f: FilterState): boolean {
  const segCount = option.flightSegments?.length ?? 0;
  const isDirect = segCount <= 1;
  if (isDirect && !f.showDirect) return false;
  if (!isDirect && !f.showConnected) return false;

  const price = optionPrice(option);
  if (price != null && price > f.maxPrice) return false;

  const dep = hourOf(optionDepartureDateTime(option));
  if (dep !== null && (dep < f.depHourRange[0] || dep > f.depHourRange[1])) return false;
  const arr = hourOf(optionArrivalDateTime(option));
  if (arr !== null && (arr < f.arrHourRange[0] || arr > f.arrHourRange[1])) return false;

  const optAirlines = optionAirlines(option);
  if (optAirlines.length && !optAirlines.every((a) => f.selectedAirlines.has(a.code))) return false;

  return true;
}

function sortOptions(rows: OriginDestinationOption[], sortBy: SortOption): OriginDestinationOption[] {
  const copy = [...rows];
  switch (sortBy) {
    case 'asc-price':
      return copy.sort((a, b) => (optionPrice(a) ?? Infinity) - (optionPrice(b) ?? Infinity));
    case 'desc-price':
      return copy.sort((a, b) => (optionPrice(b) ?? -Infinity) - (optionPrice(a) ?? -Infinity));
    case 'dep-early-to-late':
      return copy.sort((a, b) => timeOf(optionDepartureDateTime(a)) - timeOf(optionDepartureDateTime(b)));
    case 'dep-late-to-early':
      return copy.sort((a, b) => timeOf(optionDepartureDateTime(b)) - timeOf(optionDepartureDateTime(a)));
    case 'arr-early-to-late':
      return copy.sort((a, b) => timeOf(optionArrivalDateTime(a)) - timeOf(optionArrivalDateTime(b)));
    case 'arr-late-to-early':
      return copy.sort((a, b) => timeOf(optionArrivalDateTime(b)) - timeOf(optionArrivalDateTime(a)));
    default:
      return copy;
  }
}

/** Çok noktalı sonuç bloğundaki bir bacağın başlığı — sağlayıcının `parkur` alanını
 *  ("IST-ESB") tercih eder, yoksa ilk itinerary'nin ilk/son segmentinden "IST → ESB"
 *  üretir, o da yoksa sıra numarasına düşer. */
function multiCityLegLabel(leg: OriginDestinationOption[], index: number): string {
  const parkur = leg.find((o) => o.parkur)?.parkur;
  if (parkur) return `${index + 1}. Uçuş: ${parkur.replace(/-/g, ' → ')}`;
  const segments = leg[0]?.flightSegments ?? [];
  const from = segments[0]?.departureAirportCode;
  const to = segments[segments.length - 1]?.arrivalAirportCode;
  return from && to ? `${index + 1}. Uçuş: ${from} → ${to}` : `${index + 1}. Uçuş`;
}

export function AirResultsList({
  result,
  loading,
  onSelect,
  onSelectRoundTrip,
  onSelectMultiCity,
}: {
  result: AirSearchResponse | undefined;
  loading: boolean;
  /** One-way / international round-trip (single combined itinerary) selection. */
  onSelect: (option: OriginDestinationOption, fare: SelectableFare) => void;
  /** Domestic round-trip (see `optionDirection`) — called once the user has picked
   *  one fare from the Gidiş list and one from the Dönüş list and confirmed. */
  onSelectRoundTrip?: (
    departure: { option: OriginDestinationOption; fare: SelectableFare },
    ret: { option: OriginDestinationOption; fare: SelectableFare }
  ) => void;
  /** Çok noktalı (multi-city) — `result.multiOriginDestinationOptions` bacak başına
   *  sonuç dizisi taşıdığında, her bacaktan bir ücret seçilip onaylandığında çağrılır. */
  onSelectMultiCity?: (
    selections: { option: OriginDestinationOption; fare: SelectableFare }[]
  ) => void;
}) {
  const multiLegs = useMemo(
    () => (result?.multiOriginDestinationOptions ?? []).filter((leg) => leg.length > 0),
    [result]
  );
  const isMultiCity = multiLegs.length > 0 && !!onSelectMultiCity;

  // Çok noktalıda backend `originDestinationOptions`'ı boş dizi olarak da döndürebilir
  // (`?? ` boş diziyi geçmez) — o yüzden düz liste dolu değilse bacakların birleşimine düş.
  const options = useMemo(() => {
    const flat = result?.originDestinationOptions;
    if (flat?.length) return flat;
    if (isMultiCity) return multiLegs.flat();
    return flat ?? [];
  }, [result, isMultiCity, multiLegs]);

  const hasDirectionSplit = useMemo(() => {
    const directions = new Set(options.map(optionDirection).filter((d): d is number => d !== undefined));
    return directions.has(0) && directions.has(1);
  }, [options]);
  const useSplitView = hasDirectionSplit && !!onSelectRoundTrip;

  const [selectedDeparture, setSelectedDeparture] = useState<{
    option: OriginDestinationOption;
    fare: SelectableFare;
  } | null>(null);
  const [selectedReturn, setSelectedReturn] = useState<{
    option: OriginDestinationOption;
    fare: SelectableFare;
  } | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  /** "Teklif Oluştur" — `Document/Offer` (`AirSearchResponse.offerId` zorunlu). Dönen HTML
   *  yeni sekmede açılır + yazdırma penceresi (mevcut `openHtmlDocument` deseni, A10). */
  const offerMutation = useMutation({
    mutationFn: async () => {
      if (!result?.offerId) throw new Error('Teklif kimliği bulunamadı — arama tekrar yapılmalı.');
      const res = await getOfferDocument({ offerId: result.offerId });
      return res.html;
    },
    onSuccess: (html) => {
      if (html) openHtmlDocument(html, true);
      else enqueueSnackbar('Teklif belgesi alınamadı.', { variant: 'warning' });
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Teklif oluşturulamadı.', { variant: 'error' }),
  });

  /** İç hat gidiş-dönüş: gidiş seçilince `Air/Search/Combinable` ile tek-yön-birleştirilebilir
   *  dönüş opsiyonları çekilir; kullanıcı "Arama sonuçları" / "Birleştirilebilir" sekmesiyle
   *  hangi listeyi göreceğini seçer. Seçim yine `onSelectRoundTrip`'e iki `AllocateItem`
   *  olarak gider (`runAllocate` combinable-kaynaklı `option.id`'yi de aynen kullanır). */
  const [returnSource, setReturnSource] = useState<'search' | 'combinable'>('search');
  const combinableMutation = useMutation({
    mutationFn: (dep: { option: OriginDestinationOption; fare: SelectableFare }) => {
      const pid = dep.option.id ?? dep.option.flightSegments?.[0]?.productId;
      if (!pid) return Promise.reject(new Error('Gidiş uçuşunun ürün kimliği bulunamadı.'));
      if (!dep.fare.fareCode) return Promise.reject(new Error('Gidiş ücret kodu bulunamadı.'));
      return searchAirCombinable({
        allocateItems: [{ productId: pid, fareCode: dep.fare.fareCode }],
        createShoppingCart: false,
      });
    },
  });
  const combinableFresh =
    !!combinableMutation.data && combinableMutation.variables?.option === selectedDeparture?.option;
  const combinableReturns = combinableFresh ? combinableMutation.data?.originDestinationOptions ?? [] : [];

  const priceBounds = useMemo(() => {
    const prices = options.map(optionPrice).filter((p): p is number => p != null);
    if (!prices.length) return { min: 0, max: 0 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [options]);

  const airlines = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((o) => optionAirlines(o).forEach(({ code, name }) => map.set(code, name)));
    return Array.from(map, ([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [options]);

  // `AirResultsList` unmounts/remounts each time the flow leaves and re-enters the
  // 'allocate' step (see AirFlowPage's conditional render), so a fresh search always
  // gets fresh filter defaults — no explicit reset-on-new-result-set logic needed.
  const [filters, setFilters] = useState<FilterState | null>(null);

  const activeFilters: FilterState = useMemo(
    () =>
      filters ?? {
        sortBy: 'asc-price',
        showDirect: true,
        showConnected: true,
        maxPrice: priceBounds.max,
        depHourRange: [0, 24],
        arrHourRange: [0, 24],
        selectedAirlines: new Set(airlines.map((a) => a.code)),
      },
    [filters, priceBounds.max, airlines]
  );

  const setFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters({ ...activeFilters, [key]: value });
  };

  const sorted = useMemo(
    () => sortOptions(options.filter((o) => optionMatchesFilters(o, activeFilters)), activeFilters.sortBy),
    [options, activeFilters]
  );

  const departureOptions = useMemo(() => sorted.filter((o) => optionDirection(o) === 0), [sorted]);
  const returnOptions = useMemo(() => sorted.filter((o) => optionDirection(o) === 1), [sorted]);

  // Çok noktalı: her bacak kendi sonuç listesiyle, aynı filtre/sıralama uygulanmış hâlde.
  const multiCityLegs = useMemo(
    () =>
      multiLegs.map((leg) =>
        sortOptions(leg.filter((o) => optionMatchesFilters(o, activeFilters)), activeFilters.sortBy)
      ),
    [multiLegs, activeFilters]
  );
  const [multiCitySelected, setMultiCitySelected] = useState<
    Record<number, { option: OriginDestinationOption; fare: SelectableFare }>
  >({});
  const allLegsSelected =
    multiLegs.length > 0 && multiLegs.every((_, i) => multiCitySelected[i]);
  /** Seçilen itinerary aktif filtreyle o bacağın görünür listesinden çıktı mı? Seçim
   *  hâlâ geçerli (booking'e gidebilir), sadece kartı ekranda görünmüyor — özet satırında
   *  uyarı olarak işaretlenir. */
  const legSelectionFilteredOut = (i: number): boolean => {
    const sel = multiCitySelected[i];
    return !!sel && !!multiCityLegs[i] && !multiCityLegs[i].includes(sel.option);
  };

  if (!result) {
    return <Alert severity="info">Sonuçları görmek için önce arama yapın.</Alert>;
  }

  if (!options.length && !isMultiCity) {
    return <Alert severity="warning">Aramanıza uygun uçuş bulunamadı.</Alert>;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            fullWidth
            label="Sırala"
            value={activeFilters.sortBy}
            onChange={(e) => setFilter('sortBy', e.target.value as SortOption)}
          >
            {Object.entries(SORT_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Filtre
            </Typography>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activeFilters.showDirect}
                    onChange={(e) => setFilter('showDirect', e.target.checked)}
                  />
                }
                label="Direkt"
              />
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => setFilters({ ...activeFilters, showDirect: true, showConnected: false })}
              >
                sadece
              </Link>
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activeFilters.showConnected}
                    onChange={(e) => setFilter('showConnected', e.target.checked)}
                  />
                }
                label="1+ aktarma"
              />
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => setFilters({ ...activeFilters, showDirect: false, showConnected: true })}
              >
                sadece
              </Link>
            </Stack>
          </Paper>

          {priceBounds.max > priceBounds.min && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Bilet fiyatı
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Maks. {activeFilters.maxPrice.toLocaleString('tr-TR')} TL
              </Typography>
              <Slider
                size="small"
                min={priceBounds.min}
                max={priceBounds.max}
                value={activeFilters.maxPrice}
                onChange={(_, value) => setFilter('maxPrice', value as number)}
              />
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Kalkış / Varış saatleri
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Kalkış: {Math.floor(activeFilters.depHourRange[0])}:00 - {Math.floor(activeFilters.depHourRange[1])}:00
            </Typography>
            <Slider
              size="small"
              min={0}
              max={24}
              value={activeFilters.depHourRange}
              onChange={(_, value) => setFilter('depHourRange', value as [number, number])}
            />
            <Typography variant="caption" color="text.secondary">
              Varış: {Math.floor(activeFilters.arrHourRange[0])}:00 - {Math.floor(activeFilters.arrHourRange[1])}:00
            </Typography>
            <Slider
              size="small"
              min={0}
              max={24}
              value={activeFilters.arrHourRange}
              onChange={(_, value) => setFilter('arrHourRange', value as [number, number])}
            />
          </Paper>

          {airlines.length > 0 && (
            <AirlineFilterPanel
              airlines={airlines}
              selected={activeFilters.selectedAirlines}
              onChange={(next) => setFilter('selectedAirlines', next)}
            />
          )}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 9 }}>
        {result?.offerId && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
          >
            <ApiErrorAlert error={offerMutation.error} sx={{ flex: 1, m: 0 }} />
            <Button
              variant="outlined"
              size="small"
              disabled={offerMutation.isPending}
              onClick={() => offerMutation.mutate()}
            >
              {offerMutation.isPending ? 'Hazırlanıyor…' : 'Teklif Oluştur'}
            </Button>
          </Stack>
        )}
        {isMultiCity ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Çok noktalı aramada her uçuş için ayrı bir ücret seçin; tüm bacaklar seçilince devam edin.
            </Typography>

            {multiCityLegs.map((leg, legIdx) => {
              const chosen = multiCitySelected[legIdx];
              return (
                <Box key={legIdx}>
                  <Typography variant="subtitle1" gutterBottom>
                    {multiCityLegLabel(multiLegs[legIdx], legIdx)} ({leg.length})
                  </Typography>
                  <Stack spacing={2}>
                    {leg.length === 0 && (
                      <Alert severity="info">
                        {multiLegs[legIdx].length === 0
                          ? 'Bu uçuş için sonuç bulunamadı.'
                          : 'Filtrelere uygun uçuş bulunamadı.'}
                      </Alert>
                    )}
                    {leg.map((option, idx) => (
                      <OptionCard
                        key={`${option.id ?? 'opt'}-${idx}`}
                        option={option}
                        disabled={loading}
                        selectedFare={chosen?.option === option ? chosen.fare : undefined}
                        onSelectFare={(fare) =>
                          setMultiCitySelected((prev) => ({ ...prev, [legIdx]: { option, fare } }))
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}

            <Paper elevation={3} sx={{ p: 2, position: { md: 'sticky' }, bottom: 0 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack spacing={0.5}>
                  {multiLegs.map((_, legIdx) => {
                    const chosen = multiCitySelected[legIdx];
                    const price = chosen ? fareDisplayPrice(chosen.fare) : null;
                    return (
                      <Typography key={legIdx} variant="body2">
                        {legIdx + 1}. Uçuş:{' '}
                        {chosen && price
                          ? `${chosen.fare.fareName ?? chosen.fare.fareCode} — ${price.primary}`
                          : 'Seçilmedi'}
                        {legSelectionFilteredOut(legIdx) && (
                          <Typography component="span" variant="caption" color="warning.main">
                            {' '}
                            (filtre dışı — kartı görmek için filtreyi gevşetin)
                          </Typography>
                        )}
                      </Typography>
                    );
                  })}
                </Stack>
                <Button
                  variant="contained"
                  disabled={loading || !allLegsSelected}
                  onClick={() =>
                    allLegsSelected &&
                    onSelectMultiCity?.(multiLegs.map((_, legIdx) => multiCitySelected[legIdx]))
                  }
                >
                  Devam Et
                </Button>
              </Stack>
            </Paper>
          </Box>
        ) : useSplitView ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Bu iç hat gidiş-dönüş aramasında gidiş ve dönüş uçuşları farklı sağlayıcılardan olabilir —
              her ikisini de ayrı ayrı seçip devam edin.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Gidiş Uçuşları ({departureOptions.length})
                </Typography>
                <Stack spacing={2}>
                  {departureOptions.length === 0 && (
                    <Alert severity="info">Filtrelere uygun gidiş uçuşu bulunamadı.</Alert>
                  )}
                  {departureOptions.map((option, idx) => (
                    <OptionCard
                      key={`${option.id ?? 'opt'}-${idx}`}
                      option={option}
                      disabled={loading}
                      selectedFare={
                        selectedDeparture?.option === option ? selectedDeparture.fare : undefined
                      }
                      onSelectFare={(fare) => setSelectedDeparture({ option, fare })}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {(() => {
                  const returnList = returnSource === 'combinable' ? combinableReturns : returnOptions;
                  return (
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Dönüş Uçuşları ({returnList.length})
                      </Typography>

                      {selectedDeparture && (
                        <Stack spacing={1} sx={{ mb: 1 }}>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={returnSource}
                            onChange={(_, next) => next && setReturnSource(next)}
                          >
                            <ToggleButton value="search">Arama sonuçları</ToggleButton>
                            <ToggleButton value="combinable">
                              Birleştirilebilir{combinableFresh ? ` (${combinableReturns.length})` : ''}
                            </ToggleButton>
                          </ToggleButtonGroup>
                          {returnSource === 'combinable' && !combinableFresh && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={combinableMutation.isPending}
                              onClick={() => combinableMutation.mutate(selectedDeparture)}
                            >
                              {combinableMutation.isPending
                                ? 'Getiriliyor…'
                                : 'Birleştirilebilir dönüşleri getir'}
                            </Button>
                          )}
                          {returnSource === 'combinable' && combinableMutation.isPending && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                              <CircularProgress size={22} />
                            </Box>
                          )}
                          <ApiErrorAlert error={combinableMutation.error} />
                        </Stack>
                      )}

                      <Stack spacing={2}>
                        {returnList.length === 0 && (
                          <Alert severity="info">
                            {returnSource === 'combinable'
                              ? 'Birleştirilebilir dönüş opsiyonu bulunamadı.'
                              : 'Filtrelere uygun dönüş uçuşu bulunamadı.'}
                          </Alert>
                        )}
                        {returnList.map((option, idx) => (
                          <OptionCard
                            key={`${option.id ?? 'opt'}-${idx}`}
                            option={option}
                            disabled={loading}
                            selectedFare={
                              selectedReturn?.option === option ? selectedReturn.fare : undefined
                            }
                            onSelectFare={(fare) => setSelectedReturn({ option, fare })}
                          />
                        ))}
                      </Stack>
                    </>
                  );
                })()}
              </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 2, position: { md: 'sticky' }, bottom: 0 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Gidiş:{' '}
                    {selectedDeparture
                      ? `${selectedDeparture.fare.fareName ?? selectedDeparture.fare.fareCode} — ${fareDisplayPrice(selectedDeparture.fare).primary}`
                      : 'Seçilmedi'}
                  </Typography>
                  <Typography variant="body2">
                    Dönüş:{' '}
                    {selectedReturn
                      ? `${selectedReturn.fare.fareName ?? selectedReturn.fare.fareCode} — ${fareDisplayPrice(selectedReturn.fare).primary}`
                      : 'Seçilmedi'}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  disabled={loading || !selectedDeparture || !selectedReturn}
                  onClick={() =>
                    selectedDeparture &&
                    selectedReturn &&
                    onSelectRoundTrip?.(selectedDeparture, selectedReturn)
                  }
                >
                  Devam Et
                </Button>
              </Stack>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {sorted.length} / {options.length} sonuç gösteriliyor. Devam etmek için bir ücret seçeneği seçin.
            </Typography>
            {sorted.length === 0 && <Alert severity="info">Filtrelere uygun uçuş bulunamadı.</Alert>}
            {sorted.map((option, idx) => (
              <OptionCard
                key={option.id ?? idx}
                option={option}
                disabled={loading}
                onSelectFare={(fare) => onSelect(option, fare)}
              />
            ))}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
